import { Injectable } from "@angular/core";
import { dayBounds, minutesSinceMidnightLocal } from "../utils/time.utils";
import { Instructor } from "../models";

type Interval = { s: number; e: number }; // minutes [s, e)

@Injectable({ providedIn: "root" })
export class AvailabilityService {
  /* -------------------- Utils intervalles -------------------- */
  private merge(list: Interval[]): Interval[] {
    if (!list.length) return [];
    const arr = [...list].sort((a, b) => a.s - b.s);
    const res: Interval[] = [{ ...arr[0] }];
    for (let i = 1; i < arr.length; i++) {
      const last = res[res.length - 1];
      const cur = arr[i];
      if (cur.s <= last.e) last.e = Math.max(last.e, cur.e);
      else res.push({ ...cur });
    }
    return res;
  }

  private intersect(a: Interval, b: Interval): Interval | null {
    const s = Math.max(a.s, b.s);
    const e = Math.min(a.e, b.e);
    return e > s ? { s, e } : null;
  }

  private subtract(base: Interval[], cover: Interval[]): Interval[] {
    const out: Interval[] = [];
    for (const b of base) {
      let parts: Interval[] = [{ ...b }];
      for (const c of cover) {
        const next: Interval[] = [];
        for (const x of parts) {
          if (c.e <= x.s || c.s >= x.e) {
            next.push(x);
            continue;
          }
          if (c.s > x.s) next.push({ s: x.s, e: c.s });
          if (c.e < x.e) next.push({ s: c.e, e: x.e });
        }
        parts = next;
        if (!parts.length) break;
      }
      out.push(...parts);
    }
    return this.merge(out);
  }

  /* ------------- Projection au jour courant (local) ------------- */
  private projectDay(
    i: Instructor,
    currentDate: Date,
    kind: "AVAILABLE" | "UNAVAILABLE"
  ): Interval[] {
    const { dayStart, dayEnd } = dayBounds(currentDate);
    const out: Interval[] = [];

    for (const a of i.availabilities ?? []) {
      const k = String((a as any).kind ?? "").toUpperCase();
      if (k !== kind) continue;

      const s = new Date(a.start);
      const e = new Date(a.end);
      if (!(e > s)) continue;

      const sC = s < dayStart ? dayStart : s;
      const eC = e > dayEnd ? dayEnd : e;
      if (eC > sC) {
        out.push({
          s: minutesSinceMidnightLocal(sC),
          e: minutesSinceMidnightLocal(eC),
        });
      }
    }
    return this.merge(out);
  }

  /* -------- Indisponibilités sur la plage [open..close) -------- */
  private buildUnavailability(
    i: Instructor,
    currentDate: Date,
    openHour: number,
    closeHour: number
  ): Interval[] {
    const span: Interval = { s: openHour * 60, e: closeHour * 60 };

    if (i.availabilityPolicy === "AVAILABLE_BY_DEFAULT") {
      // indispos = union fenêtres UNAVAILABLE ∩ span
      const unav = this.projectDay(i, currentDate, "UNAVAILABLE")
        .map((iv) => this.intersect(iv, span))
        .filter((iv): iv is Interval => !!iv);
      return this.merge(unav);
    }

    // UNAVAILABLE_BY_DEFAULT → indispos = span \ union(fenêtres AVAILABLE ∩ span)
    const avail = this.projectDay(i, currentDate, "AVAILABLE")
      .map((iv) => this.intersect(iv, span))
      .filter((iv): iv is Interval => !!iv);
    return this.subtract([span], this.merge(avail));
  }

  /* -------------------- API publique utile -------------------- */

  /** Classe CSS pour une case horaire [h, h+1) (dispo / indispo / mixte) */
  availabilityClass(
    i: Instructor,
    h: number,
    open: number,
    close: number,
    _step: number, // non utilisé ici
    currentDate: Date
  ): "avail-available" | "avail-unavailable" | "avail-mixed" | "" {
    const cell: Interval = {
      s: Math.max(open * 60, h * 60),
      e: Math.min(close * 60, (h + 1) * 60),
    };
    if (cell.e <= cell.s) return "";

    const unav = this.buildUnavailability(i, currentDate, open, close);

    let overlap = 0;
    for (const u of unav) {
      const iv = this.intersect(cell, u);
      if (iv) {
        overlap += iv.e - iv.s;
        if (overlap >= cell.e - cell.s) break;
      }
    }

    if (overlap === 0) return "avail-available"; // 0% indispo
    if (overlap >= cell.e - cell.s) return "avail-unavailable"; // 100% indispo
    return "avail-mixed"; // partiel
  }

  /** True si **toute** la fenêtre [startMin, endMin) est disponible */
  isFullyAvailable(
    i: Instructor,
    currentDate: Date,
    startMin: number,
    endMin: number,
    open: number,
    close: number
  ): boolean {
    const span: Interval = {
      s: Math.max(open * 60, startMin),
      e: Math.min(close * 60, endMin),
    };
    if (span.e <= span.s) return false;
    const unav = this.buildUnavailability(i, currentDate, open, close);
    for (const u of unav) {
      if (this.intersect(span, u)) return false; // un seul recouvrement suffit
    }
    return true;
  }

  /** Minutes d’overlap entre [startMin, endMin) et les indisponibilités du jour */
  overlapWithUnavailability(
    i: Instructor,
    currentDate: Date,
    startMin: number,
    endMin: number,
    open: number,
    close: number
  ): number {
    const span: Interval = {
      s: Math.max(open * 60, startMin),
      e: Math.min(close * 60, endMin),
    };
    if (span.e <= span.s) return 0;
    const unav = this.buildUnavailability(i, currentDate, open, close);

    let total = 0;
    for (const u of unav) {
      const iv = this.intersect(span, u);
      if (iv) total += iv.e - iv.s;
    }
    return total;
  }

  /** Indisponibilités **déjà fusionnées** pour le jour (utile pour affichages/tests) */
  getUnavailabilityForDay(
    i: Instructor,
    currentDate: Date,
    open: number,
    close: number
  ): Interval[] {
    return this.buildUnavailability(i, currentDate, open, close);
  }
}
